import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, BellOff, Volume2, MonitorSpeaker } from "lucide-react";
import { toast } from "sonner";
import {
  loadOrderNotifPrefs,
  saveOrderNotifPrefs,
  ensureNotificationPermission,
} from "@/hooks/useOrderNotifications";

/**
 * Botón compacto para configurar las notificaciones de nuevas órdenes.
 * Se puede colocar en el header de páginas relevantes (POS, Cola de Órdenes).
 */
export default function OrderNotificationsSettings() {
  const [prefs, setPrefs] = useState(() => loadOrderNotifPrefs());
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );

  useEffect(() => {
    saveOrderNotifPrefs(prefs);
  }, [prefs]);

  const patch = (key, val) => setPrefs((p) => ({ ...p, [key]: val }));

  const requestPermission = async () => {
    const r = await ensureNotificationPermission();
    setPermission(r);
    if (r === "granted") toast.success("Notificaciones de escritorio activadas");
    else if (r === "denied") toast.error("Permiso denegado. Actívalo en la configuración del navegador.");
  };

  const supported = typeof Notification !== "undefined";
  const anyOn = prefs.enabled && (prefs.sound || prefs.desktop);
  const Icon = anyOn ? Bell : BellOff;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" data-testid="order-notif-trigger" title="Notificaciones de órdenes">
          <Icon className={`w-4 h-4 ${anyOn ? "text-primary" : "text-muted-foreground"}`} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="end">
        <div className="space-y-3">
          <div>
            <div className="font-semibold text-sm">Notificaciones</div>
            <p className="text-xs text-muted-foreground">Avísame cuando llegue una orden nueva</p>
          </div>

          <label className="flex items-center gap-2 cursor-pointer text-sm">
            <Checkbox
              checked={prefs.enabled}
              onCheckedChange={(v) => patch("enabled", !!v)}
              data-testid="order-notif-enabled"
            />
            <span>Activar notificaciones</span>
          </label>

          <div className="pl-6 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox
                checked={prefs.sound}
                onCheckedChange={(v) => patch("sound", !!v)}
                disabled={!prefs.enabled}
                data-testid="order-notif-sound"
              />
              <Volume2 className="w-3.5 h-3.5" />
              <span>Sonido suave</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <Checkbox
                checked={prefs.desktop}
                onCheckedChange={(v) => patch("desktop", !!v)}
                disabled={!prefs.enabled || !supported || permission === "denied"}
                data-testid="order-notif-desktop"
              />
              <MonitorSpeaker className="w-3.5 h-3.5" />
              <span>Notificación de escritorio</span>
            </label>
          </div>

          {supported && permission !== "granted" && prefs.desktop && (
            <div className="p-2 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded text-xs">
              {permission === "denied" ? (
                <span>Bloqueadas por el navegador. Actívalas manualmente en la configuración del sitio.</span>
              ) : (
                <>
                  <p className="mb-1.5">Necesitamos tu permiso para mostrar notificaciones de escritorio.</p>
                  <Button size="sm" onClick={requestPermission} className="w-full h-7 text-xs" data-testid="order-notif-permission-btn">
                    Solicitar permiso
                  </Button>
                </>
              )}
            </div>
          )}

          {!supported && (
            <p className="text-xs text-muted-foreground">Tu navegador no soporta notificaciones de escritorio.</p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
